import { useEffect, useRef, useSyncExternalStore } from 'react'

import { Icon } from '../ui/Icon'
import { useAnnounce } from '../ui/Announce'

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange)
  window.addEventListener('offline', onChange)
  return () => {
    window.removeEventListener('online', onChange)
    window.removeEventListener('offline', onChange)
  }
}

function readOnline(): boolean {
  return navigator.onLine !== false
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, readOnline, () => true)
}

/**
 * What the frame says when the connection has gone.
 *
 * A strip in the frame, not a dialog: losing the connection does not undo what is on the screen,
 * and being made to dismiss a box before carrying on would. It reports the one thing the browser
 * knows for certain; a request that fails for any other reason says so where it was made.
 */
export function Connection() {
  const online = useOnline()
  const announce = useAnnounce()
  const wasOffline = useRef(false)

  useEffect(() => {
    if (!online) {
      wasOffline.current = true
      return
    }
    if (wasOffline.current) {
      wasOffline.current = false
      announce('The connection is back.')
    }
  }, [online, announce])

  if (online) {
    return null
  }

  return (
    <div className="strip" role="status">
      <Icon name="warning" small />
      <span>You are offline. MyLib picks up again as soon as the connection is back.</span>
    </div>
  )
}
